import 'svelte/internal/disclose-version';
import * as $ from 'svelte/internal/client';
import { updateParticipant, deleteParticipant } from '$lib/api/splits';
import Button from '$lib/components/ui/button/button.svelte';
import Input from '$lib/components/ui/input/input.svelte';
import Label from '$lib/components/ui/label/label.svelte';
import ConfirmDialog from '$lib/components/ui/confirm-dialog/confirm-dialog.svelte';
import { addToast } from '$lib/stores/toastStore.svelte';
import { X, Info } from '$lib/icons';

var root_4 = $.from_html(`<p class="text-sm text-destructive"> </p>`);
var root_6 = $.from_html(`<p class="text-sm text-destructive"> </p>`);
var root_8 = $.from_html(`<p class="text-sm text-destructive"> </p>`);
var root_10 = $.from_svg(`<svg class="animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg> Saving...`, 1);
var root_16 = $.from_html(`<p class="text-sm text-muted-foreground leading-relaxed"> </p>`);
var root_14 = $.from_html(`<div class="fixed inset-0 z-[70] bg-black/50 flex items-end justify-center sm:items-center sm:p-4" role="dialog" aria-modal="true" aria-labelledby="field-info-modal-title" tabindex="-1"><div role="presentation" class="bg-background w-full sm:max-w-[420px] rounded-t-xl sm:rounded-xl shadow-lg animate-in fade-in slide-in-from-bottom-4 sm:zoom-in-95"><div class="flex items-center justify-between p-4 border-b"><h2 id="field-info-modal-title" class="text-base font-semibold"> </h2> <!></div> <div class="p-4 space-y-3"></div> <div class="p-4 border-t"><!></div></div></div>`);
var root_1 = $.from_html(`<div class="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-labelledby="edit-participant-title" tabindex="-1"><div class="bg-background rounded-lg shadow-lg w-full max-w-[420px] animate-in fade-in zoom-in-95"><div class="flex items-center justify-between p-4 border-b"><h2 id="edit-participant-title" class="text-lg font-semibold">Edit Participant</h2> <!></div> <form class="p-4 space-y-4"><div class="space-y-2"><!> <!> <!></div> <div class="flex gap-3"><div class="space-y-2 flex-1"><div class="flex items-center gap-1"><!> <button type="button" class="p-0.5 rounded-full hover:bg-muted text-muted-foreground" aria-label="Info about Nights"><!></button></div> <!> <!></div> <div class="space-y-2 flex-1"><div class="flex items-center gap-1"><!> <button type="button" class="p-0.5 rounded-full hover:bg-muted text-muted-foreground" aria-label="Info about Share"><!></button></div> <!> <!></div></div> <div class="flex gap-2 pt-2"><!> <!></div> <div class="pt-4 border-t"><!></div></form></div></div> <!>`, 1);
var root = $.from_html(`<!> <!> <!>`, 1);

export default function EditParticipantModal($$anchor, $$props) {
	$.push($$props, true);

	/**
	 * EditParticipantModal - Story FNS-002-4
	 * Modal for editing participant name and nights with delete option
	 */
	// Constants
	const AUTO_FOCUS_DELAY_MS = 50; // Delay for auto-focusing name field after modal opens

	// Field info modal state
	let activeFieldInfo = $.state(null);

	const fieldInfo = {
		nights: {
			title: 'Nights',
			description: `The number of nights this participant stays during the trip.\n\nUsed in the "By Night" split mode: costs are divided proportionally based on each participant's nights × share weight.\n\nExample: a participant staying 3 nights out of a 7-night trip pays for 3/7 of the night-based expenses (adjusted by their share).`
		},

		share: {
			title: 'Share',
			description: `The number of persons this participant represents.\n\nA solo traveller has a share of 1. A couple sharing costs would have a share of 2, a family of 3 would use 3, etc.\n\nUsed in "By Night" mode (nights × share) and "By Share" mode (share only) to calculate each participant's proportional contribution.\n\nExample: a couple (share 2) staying 3 nights counts as 6 night-shares, compared to 3 night-shares for a solo person staying the same time.`
		}
	};

	// Props interface (Svelte 5 pattern)
	// Form state
	let editName = $.state('');

	let editNights = $.state(1);
	let editShare = $.state(1);
	let isLoading = $.state(false);

	// Validation state
	let validationErrors = $.state($.proxy({}));

	let nameTouched = $.state(false);
	let nightsTouched = $.state(false);
	let shareTouched = $.state(false);

	// Confirmation dialogs
	let showDiscardConfirm = $.state(false);

	let showDeleteConfirm = $.state(false);
	let isDeleting = $.state(false);

	// Dirty tracking
	const isDirty = $.derived(() => $$props.participant != null && ($.get(editName) !== $$props.participant.name || $.get(editNights) !== $$props.participant.nights || $.get(editShare) !== $$props.participant.share));

	// Duplicate name check (case-insensitive, exclude current participant)
	const isDuplicate = $.derived(() => $$props.participants.some((p) => p.id !== $$props.participant?.id && p.name.toLowerCase() === $.get(editName).trim().toLowerCase()));

	// Validation
	const isNameValid = $.derived(() => $.get(editName).trim().length > 0 && $.get(editName).trim().length <= 50 && !$.get(isDuplicate));

	const isNightsValid = $.derived(() => $.get(editNights) >= 1 && $.get(editNights) <= 365);
	const isShareValid = $.derived(() => $.get(editShare) >= 0.5 && $.get(editShare) <= 50);
	const isValid = $.derived(() => $.get(isNameValid) && $.get(isNightsValid) && $.get(isShareValid));

	// Has expenses check for delete button
	const hasExpenses = $.derived(() => $$props.split.expenses.some((e) => e.payerId === $$props.participant?.id));

	// Reset form when modal opens or participant changes
	$.user_effect(() => {
		if ($$props.open && $$props.participant) {
			$.set(editName, $$props.participant.name, true);
			$.set(editNights, Math.max(1, Math.round($$props.participant.nights)), true);
			$.set(editShare, $$props.participant.share, true);
			$.set(validationErrors, {}, true);
			$.set(nameTouched, false);
			$.set(nightsTouched, false);
			$.set(shareTouched, false);
			$.set(showDiscardConfirm, false);
			$.set(showDeleteConfirm, false);
			$.set(isLoading, false);
			$.set(isDeleting, false);

			// Auto-focus name field
			setTimeout(
				() => {
					if (typeof document !== 'undefined') {
						document.getElementById('edit-participant-name-modal')?.focus();
					}
				},
				AUTO_FOCUS_DELAY_MS
			);
		}
	});

	function validateName() {
		if ($.get(editName).trim().length === 0) {
			$.get(validationErrors).name = 'Name is required';
		} else if ($.get(editName).trim().length > 50) {
			$.get(validationErrors).name = 'Name cannot exceed 50 characters';
		} else if ($.get(isDuplicate)) {
			$.get(validationErrors).name = 'Name must be unique (participant already exists)';
		} else {
			delete $.get(validationErrors).name;
			$.set(validationErrors, { ...$.get(validationErrors) }, true);
		}
	}

	function validateNights() {
		if ($.get(editNights) < 1) {
			$.get(validationErrors).nights = 'Nights must be at least 1';
		} else if ($.get(editNights) > 365) {
			$.get(validationErrors).nights = 'Nights cannot exceed 365';
		} else {
			delete $.get(validationErrors).nights;
			$.set(validationErrors, { ...$.get(validationErrors) }, true);
		}
	}

	function handleNameBlur() {
		$.set(nameTouched, true);
		validateName();
	}

	function handleNightsBlur() {
		$.set(nightsTouched, true);
		validateNights();
	}

	function validateShare() {
		if ($.get(editShare) < 0.5) {
			$.get(validationErrors).share = 'Must be at least 0.5';
		} else if ($.get(editShare) > 50) {
			$.get(validationErrors).share = 'Cannot exceed 50';
		} else {
			delete $.get(validationErrors).share;
			$.set(validationErrors, { ...$.get(validationErrors) }, true);
		}
	}

	function handleShareBlur() {
		$.set(shareTouched, true);
		validateShare();
	}

	async function handleSubmit() {
		if (!$$props.participant) return;

		// Touch all fields to show validation errors
		$.set(nameTouched, true);

		$.set(nightsTouched, true);
		$.set(shareTouched, true);

		// Final validation
		validateName();

		validateNights();
		validateShare();

		if (!$.get(isValid) || !$.get(isDirty)) return;
		if ($.get(isLoading)) return;

		$.set(isLoading, true);

		try {
			await updateParticipant($$props.splitId, $$props.participant.id, {
				name: $.get(editName).trim(),
				nights: $.get(editNights),
				share: $.get(editShare),

				// Preserve the current shared account grouping — it is managed from the Settlement page
				sharedAccountId: $$props.participant.sharedAccountId ?? null
			});

			addToast({
				type: 'success',
				message: 'Participant updated',
				description: `${$.get(editName).trim()} · ${$.get(editNights)} nights · share ${$.get(editShare)}`
			});

			await $$props.onSuccess();
			$$props.onClose();
		} catch(err) {
			addToast({
				type: 'error',
				message: err.detail || 'Failed to update participant. Please try again.'
			});
		} finally {
			$.set(isLoading, false);
		}
	}

	function handleCloseAttempt() {
		if ($.get(isDirty)) {
			$.set(showDiscardConfirm, true);
		} else {
			$$props.onClose();
		}
	}

	function handleConfirmDiscard() {
		$.set(showDiscardConfirm, false);
		$$props.onClose();
	}

	function handleCancelDiscard() {
		$.set(showDiscardConfirm, false);
	}

	function handleDeleteClick() {
		if ($.get(hasExpenses)) return;

		$.set(showDeleteConfirm, true);
	}

	async function handleConfirmDelete() {
		if (!$$props.participant) return;

		$.set(isDeleting, true);

		try {
			await deleteParticipant($$props.splitId, $$props.participant.id);

			addToast({
				type: 'success',
				message: 'Participant removed',
				description: $$props.participant.name
			});

			await $$props.onSuccess();
			$$props.onClose();
		} catch(err) {
			addToast({
				type: 'error',
				message: err.detail || 'Failed to remove participant. Please try again.'
			});
		} finally {
			$.set(isDeleting, false);
			$.set(showDeleteConfirm, false);
		}
	}

	function handleCancelDelete() {
		$.set(showDeleteConfirm, false);
	}

	function handleKeydown(event) {
		if (!$$props.open) return;

		if (event.key === 'Escape' && !$.get(isLoading) && !$.get(showDiscardConfirm) && !$.get(showDeleteConfirm)) {
			handleCloseAttempt();
		}

		// Focus trap - keep Tab within modal (WCAG 2.1)
		if (event.key === 'Tab') {
			const modal = document.querySelector('[role="dialog"]');

			if (!modal) return;

			const focusable = modal.querySelectorAll('button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])');

			if (focusable.length === 0) return;

			const first = focusable[0];
			const last = focusable[focusable.length - 1];

			if (event.shiftKey && document.activeElement === first) {
				event.preventDefault();
				last.focus();
			} else if (!event.shiftKey && document.activeElement === last) {
				event.preventDefault();
				first.focus();
			}
		}
	}

	function handleBackdropClick(event) {
		if (event.target === event.currentTarget && !$.get(isLoading)) {
			handleCloseAttempt();
		}
	}

	var fragment = root();

	$.event('keydown', $.window, handleKeydown);

	var node = $.first_child(fragment);

	{
		var consequent_5 = ($$anchor) => {
			var fragment_1 = root_1();
			var div = $.first_child(fragment_1);

			div.__click = handleBackdropClick;

			var div_1 = $.child(div);

			div_1.__click = (e) => e.stopPropagation();

			var div_2 = $.child(div_1);
			var node_1 = $.sibling($.child(div_2), 2);

			Button(node_1, {
				variant: 'ghost',
				size: 'sm',
				onclick: handleCloseAttempt,

				get disabled() {
					return $.get(isLoading);
				},

				class: 'min-h-[44px] min-w-[44px]',
				'aria-label': 'Close',

				children: ($$anchor, $$slotProps) => {
					X($$anchor, { class: 'h-4 w-4' });
				},

				$$slots: { default: true }
			});

			$.reset(div_2);

			var form = $.sibling(div_2, 2);
			var div_3 = $.child(form);
			var node_2 = $.child(div_3);

			Label(node_2, {
				for: 'edit-participant-name-modal',

				children: ($$anchor, $$slotProps) => {
					$.next();

					var text = $.text('Name');

					$.append($$anchor, text);
				},

				$$slots: { default: true }
			});

			var node_3 = $.sibling(node_2, 2);

			{
				let $0 = $.derived(() => $.get(nameTouched) && !!$.get(validationErrors).name);

				Input(node_3, {
					id: 'edit-participant-name-modal',
					type: 'text',
					onblur: handleNameBlur,
					class: 'min-h-[44px]',

					get 'aria-invalid'() {
						return $.get($0);
					},

					get disabled() {
						return $.get(isLoading);
					},

					get value() {
						return $.get(editName);
					},

					set value($$value) {
						$.set(editName, $$value, true);
					}
				});
			}

			var node_4 = $.sibling(node_3, 2);

			{
				var consequent = ($$anchor) => {
					var p_1 = root_4();
					var text_1 = $.child(p_1, true);

					$.reset(p_1);
					$.template_effect(() => $.set_text(text_1, $.get(validationErrors).name));
					$.append($$anchor, p_1);
				};

				$.if(node_4, ($$render) => {
					if ($.get(nameTouched) && $.get(validationErrors).name) $$render(consequent);
				});
			}

			$.reset(div_3);

			var div_4 = $.sibling(div_3, 2);
			var div_5 = $.child(div_4);
			var div_6 = $.child(div_5);
			var node_5 = $.child(div_6);

			Label(node_5, {
				for: 'edit-participant-nights-modal',

				children: ($$anchor, $$slotProps) => {
					$.next();

					var text_2 = $.text('Nights');

					$.append($$anchor, text_2);
				},

				$$slots: { default: true }
			});

			var button = $.sibling(node_5, 2);

			button.__click = () => {
				$.set(activeFieldInfo, 'nights');
			};

			var node_6 = $.child(button);

			Info(node_6, { class: 'h-3.5 w-3.5' });
			$.reset(button);
			$.reset(div_6);

			var node_7 = $.sibling(div_6, 2);

			{
				let $0 = $.derived(() => $.get(nightsTouched) && !!$.get(validationErrors).nights);

				Input(node_7, {
					id: 'edit-participant-nights-modal',
					type: 'number',
					step: '1',
					min: '1',
					max: '365',
					onblur: handleNightsBlur,
					class: 'min-h-[44px]',

					get 'aria-invalid'() {
						return $.get($0);
					},

					get disabled() {
						return $.get(isLoading);
					},

					get value() {
						return $.get(editNights);
					},

					set value($$value) {
						$.set(editNights, $$value, true);
					}
				});
			}

			var node_8 = $.sibling(node_7, 2);

			{
				var consequent_1 = ($$anchor) => {
					var p_2 = root_6();
					var text_3 = $.child(p_2, true);

					$.reset(p_2);
					$.template_effect(() => $.set_text(text_3, $.get(validationErrors).nights));
					$.append($$anchor, p_2);
				};

				$.if(node_8, ($$render) => {
					if ($.get(nightsTouched) && $.get(validationErrors).nights) $$render(consequent_1);
				});
			}

			$.reset(div_5);

			var div_7 = $.sibling(div_5, 2);
			var div_8 = $.child(div_7);
			var node_9 = $.child(div_8);

			Label(node_9, {
				for: 'edit-participant-share-modal',

				children: ($$anchor, $$slotProps) => {
					$.next();

					var text_4 = $.text('Share');

					$.append($$anchor, text_4);
				},

				$$slots: { default: true }
			});

			var button_1 = $.sibling(node_9, 2);

			button_1.__click = () => {
				$.set(activeFieldInfo, 'share');
			};

			var node_10 = $.child(button_1);

			Info(node_10, { class: 'h-3.5 w-3.5' });
			$.reset(button_1);
			$.reset(div_8);

			var node_11 = $.sibling(div_8, 2);

			{
				let $0 = $.derived(() => $.get(shareTouched) && !!$.get(validationErrors).share);

				Input(node_11, {
					id: 'edit-participant-share-modal',
					type: 'number',
					step: 'any',
					min: '0.5',
					max: '50',
					onblur: handleShareBlur,

					onkeydown: (e) => {
						if (e.key === 'ArrowUp') {
							e.preventDefault();
							$.set(editShare, Math.min(50, Math.round(($.get(editShare) + 0.5) * 100) / 100), true);
						} else if (e.key === 'ArrowDown') {
							e.preventDefault();
							$.set(editShare, Math.max(0.5, Math.round(($.get(editShare) - 0.5) * 100) / 100), true);
						}
					},

					class: 'min-h-[44px]',

					get 'aria-invalid'() {
						return $.get($0);
					},

					get disabled() {
						return $.get(isLoading);
					},

					get value() {
						return $.get(editShare);
					},

					set value($$value) {
						$.set(editShare, $$value, true);
					}
				});
			}

			var node_12 = $.sibling(node_11, 2);

			{
				var consequent_2 = ($$anchor) => {
					var p_3 = root_8();
					var text_5 = $.child(p_3, true);

					$.reset(p_3);
					$.template_effect(() => $.set_text(text_5, $.get(validationErrors).share));
					$.append($$anchor, p_3);
				};

				$.if(node_12, ($$render) => {
					if ($.get(shareTouched) && $.get(validationErrors).share) $$render(consequent_2);
				});
			}

			$.reset(div_7);
			$.reset(div_4);

			var div_9 = $.sibling(div_4, 2);
			var node_13 = $.child(div_9);

			{
				let $0 = $.derived(() => !$.get(isDirty) || !$.get(isValid) || $.get(isLoading));

				Button(node_13, {
					type: 'submit',

					get disabled() {
						return $.get($0);
					},

					class: 'flex-1 min-h-[44px]',

					children: ($$anchor, $$slotProps) => {
						var fragment_3 = $.comment();
						var node_14 = $.first_child(fragment_3);

						{
							var consequent_3 = ($$anchor) => {
								var fragment_4 = root_10();

								$.next();
								$.append($$anchor, fragment_4);
							};

							var alternate = ($$anchor) => {
								var text_6 = $.text('Save Changes');

								$.append($$anchor, text_6);
							};

							$.if(node_14, ($$render) => {
								if ($.get(isLoading)) $$render(consequent_3); else $$render(alternate, false);
							});
						}

						$.append($$anchor, fragment_3);
					},

					$$slots: { default: true }
				});
			}

			var node_15 = $.sibling(node_13, 2);

			Button(node_15, {
				type: 'button',
				variant: 'outline',
				onclick: handleCloseAttempt,

				get disabled() {
					return $.get(isLoading);
				},

				class: 'flex-1 min-h-[44px]',
				'aria-label': 'Cancel edit',

				children: ($$anchor, $$slotProps) => {
					$.next();

					var text_7 = $.text('Cancel');

					$.append($$anchor, text_7);
				},

				$$slots: { default: true }
			});

			$.reset(div_9);

			var div_10 = $.sibling(div_9, 2);
			var node_16 = $.child(div_10);

			{
				let $0 = $.derived(() => $.get(hasExpenses) || $.get(isLoading));
				let $1 = $.derived(() => $.get(hasExpenses) ? 'Remove expenses first' : '');

				Button(node_16, {
					type: 'button',
					variant: 'destructive',
					class: 'w-full min-h-[44px]',

					get disabled() {
						return $.get($0);
					},

					onclick: handleDeleteClick,

					get title() {
						return $.get($1);
					},

					children: ($$anchor, $$slotProps) => {
						$.next();

						var text_8 = $.text('Delete Participant');

						$.append($$anchor, text_8);
					},

					$$slots: { default: true }
				});
			}

			$.reset(div_10);
			$.reset(form);
			$.reset(div_1);
			$.reset(div);

			var node_17 = $.sibling(div, 2);

			{
				var consequent_4 = ($$anchor) => {
					var div_11 = root_14();

					div_11.__click = () => {
						$.set(activeFieldInfo, null);
					};

					var div_12 = $.child(div_11);

					div_12.__click = (e) => e.stopPropagation();

					var div_13 = $.child(div_12);
					var h2 = $.child(div_13);
					var text_9 = $.child(h2, true);

					$.reset(h2);

					var node_18 = $.sibling(h2, 2);

					Button(node_18, {
						variant: 'ghost',
						size: 'sm',

						onclick: () => {
							$.set(activeFieldInfo, null);
						},

						class: 'min-h-[44px] min-w-[44px]',
						'aria-label': 'Close',

						children: ($$anchor, $$slotProps) => {
							X($$anchor, { class: 'h-4 w-4' });
						},

						$$slots: { default: true }
					});

					$.reset(div_13);

					var div_14 = $.sibling(div_13, 2);

					$.each(div_14, 21, () => fieldInfo[$.get(activeFieldInfo)].description.split('\n\n'), $.index, ($$anchor, paragraph) => {
						var p_4 = root_16();
						var text_10 = $.child(p_4, true);

						$.reset(p_4);
						$.template_effect(() => $.set_text(text_10, $.get(paragraph)));
						$.append($$anchor, p_4);
					});

					$.reset(div_14);

					var div_15 = $.sibling(div_14, 2);
					var node_19 = $.child(div_15);

					Button(node_19, {
						onclick: () => {
							$.set(activeFieldInfo, null);
						},

						class: 'w-full min-h-[44px]',

						children: ($$anchor, $$slotProps) => {
							$.next();

							var text_11 = $.text('Got it');

							$.append($$anchor, text_11);
						},

						$$slots: { default: true }
					});

					$.reset(div_15);
					$.reset(div_12);
					$.reset(div_11);
					$.template_effect(() => $.set_text(text_9, fieldInfo[$.get(activeFieldInfo)].title));
					$.append($$anchor, div_11);
				};

				$.if(node_17, ($$render) => {
					if ($.get(activeFieldInfo)) $$render(consequent_4);
				});
			}

			$.event('submit', form, (e) => {
				e.preventDefault();
				handleSubmit();
			});

			$.append($$anchor, fragment_1);
		};

		$.if(node, ($$render) => {
			if ($$props.open) $$render(consequent_5);
		});
	}

	var node_20 = $.sibling(node, 2);

	ConfirmDialog(node_20, {
		get open() {
			return $.get(showDiscardConfirm);
		},

		title: 'Discard changes?',
		description: 'You have unsaved changes. Are you sure you want to close?',
		confirmLabel: 'Discard',
		cancelLabel: 'Keep editing',
		onConfirm: handleConfirmDiscard,
		onCancel: handleCancelDiscard
	});

	var node_21 = $.sibling(node_20, 2);

	{
		let $0 = $.derived(() => $$props.participant
			? `Delete ${$$props.participant.name}?`
			: 'Delete participant?');

		ConfirmDialog(node_21, {
			get open() {
				return $.get(showDeleteConfirm);
			},

			get title() {
				return $.get($0);
			},

			description: 'This cannot be undone.',
			confirmLabel: 'Remove',
			cancelLabel: 'Cancel',

			get isLoading() {
				return $.get(isDeleting);
			},

			onConfirm: handleConfirmDelete,
			onCancel: handleCancelDelete
		});
	}

	$.append($$anchor, fragment);
	$.pop();
}

$.delegate(['click']);
//# sourceMappingURL=data:application/json;charset=utf-8;base64,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